import React from 'react';

import { Card, CardHeader, CardContent, CardActions, Typography, CardProps } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';

import { mergeSx } from '@/theme/mergeSx';

interface InfoCardProps extends Omit<CardProps, 'title'> {
  title?: string | React.ReactNode;
  children: React.ReactNode;
  actions?: React.ReactNode;
  /** Displays controls beside the title in the card header. */
  headerAction?: React.ReactNode;
  /** Customizes the content area's spacing for a specific card usage. */
  contentSx?: SxProps<Theme>;
  testId?: string;
}

/**
 * InfoCard - A simple card component for displaying static information sections
 * Used throughout the app for organizing content into visual sections
 */
export const InfoCard: React.FC<InfoCardProps> = ({
  title,
  children,
  actions,
  headerAction,
  contentSx,
  sx,
  testId,
  ...props
}) => {
  return (
    <Card {...props} data-testid={testId} sx={sx}>
      {title && (
        <CardHeader
          action={headerAction}
          slotProps={{ action: { sx: { alignSelf: 'center', m: 0 } } }}
          title={
            typeof title === 'string' ? (
              <Typography variant="h6" component="h2">
                {title}
              </Typography>
            ) : (
              title
            )
          }
          sx={{ pb: 0 }}
        />
      )}
      <CardContent sx={mergeSx({ p: 2, '&:last-child': { pb: 2 } }, contentSx)}>
        {children}
      </CardContent>
      {actions ? <CardActions sx={{ px: 2, pb: 2, pt: 0 }}>{actions}</CardActions> : null}
    </Card>
  );
};
